import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { useState } from "react";

import { useEditorModal } from "@/components/editor/editor-hooks/use-modal";
import { ContentEditable } from "@/components/editor/editor-ui/content-editable";
import { editorTheme } from "@/components/editor/themes/editor-theme";

// Link plugins
import { AutoLinkPlugin } from "@/components/editor/plugins/auto-link-plugin";
import { FloatingLinkEditorPlugin } from "@/components/editor/plugins/floating-link-editor-plugin";
import { LinkPlugin } from "@/components/editor/plugins/link-plugin";

// Image plugin
import { ImagesPlugin } from "@/components/editor/plugins/images-plugin";

// Embed plugins
import { AutoEmbedPlugin } from "@/components/editor/plugins/embeds/auto-embed-plugin";
import { TwitterPlugin } from "@/components/editor/plugins/embeds/twitter-plugin";
import { YouTubePlugin } from "@/components/editor/plugins/embeds/youtube-plugin";

// Table plugin
import {
  TablePlugin as CustomTablePlugin,
  TableContext,
} from "@/components/editor/plugins/table-plugin";

// Toolbar plugins
import { BlockFormatDropDown } from "@/components/editor/plugins/toolbar/block-format-toolbar-plugin";
import { FormatBulletedList } from "@/components/editor/plugins/toolbar/block-format/format-bulleted-list";
import { FormatCheckList } from "@/components/editor/plugins/toolbar/block-format/format-check-list";
import { FormatHeading } from "@/components/editor/plugins/toolbar/block-format/format-heading";
import { FormatNumberedList } from "@/components/editor/plugins/toolbar/block-format/format-numbered-list";
import { FormatParagraph } from "@/components/editor/plugins/toolbar/block-format/format-paragraph";
import { FormatQuote } from "@/components/editor/plugins/toolbar/block-format/format-quote";
import { BlockInsertPlugin } from "@/components/editor/plugins/toolbar/block-insert-plugin";
import { InsertEmbeds } from "@/components/editor/plugins/toolbar/block-insert/insert-embeds";
import { InsertHorizontalRule } from "@/components/editor/plugins/toolbar/block-insert/insert-horizontal-rule";
import { InsertImage } from "@/components/editor/plugins/toolbar/block-insert/insert-image";
import { InsertTable } from "@/components/editor/plugins/toolbar/block-insert/insert-table";
import { ElementFormatToolbarPlugin } from "@/components/editor/plugins/toolbar/element-format-toolbar-plugin";
import { FontBackgroundToolbarPlugin } from "@/components/editor/plugins/toolbar/font-background-toolbar-plugin";
import { FontColorToolbarPlugin } from "@/components/editor/plugins/toolbar/font-color-toolbar-plugin";
import { FontFamilyToolbarPlugin } from "@/components/editor/plugins/toolbar/font-family-toolbar-plugin";
import { FontSizeToolbarPlugin } from "@/components/editor/plugins/toolbar/font-size-toolbar-plugin";
import { HistoryToolbarPlugin } from "@/components/editor/plugins/toolbar/history-toolbar-plugin";
import { ToolbarPlugin } from "@/components/editor/plugins/toolbar/toolbar-plugin";

// Action plugins
import { ImportExportPlugin } from "@/components/editor/plugins/actions/import-export-plugin";
import { MarkdownImportPlugin } from "@/components/editor/plugins/actions/markdown-import-plugin";
import { MarkdownTogglePlugin } from "@/components/editor/plugins/actions/markdown-toggle-plugin";
import { PLAYGROUND_TRANSFORMERS } from "@/components/editor/transformers/transformers";

import { CodeHighlightPlugin } from "@/components/editor/plugins/code-highlight-plugin";
// Draggable block plugin
import { DraggableBlockPlugin } from "@/components/editor/plugins/draggable-block-plugin";

const placeholder = "Essay içeriğinizi buraya yazın...";

export function Plugins({ readOnly = false }: { readOnly?: boolean }) {
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);
  const [modal, _showModal] = useEditorModal();

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  return (
    <TableContext>
      <div className="relative">
        {/* Toolbar */}
        {!readOnly && (
          <ToolbarPlugin>
            {() => (
              <div className="sticky top-0 z-10 flex flex-wrap gap-2 overflow-auto border-b p-1">
                {/* History (Undo/Redo) */}
                <HistoryToolbarPlugin />

                {/* Block Format Dropdown */}
                <BlockFormatDropDown>
                  <FormatParagraph />
                  <FormatHeading levels={["h1", "h2", "h3"]} />
                  <FormatNumberedList />
                  <FormatBulletedList />
                  <FormatCheckList />
                  <FormatQuote />
                </BlockFormatDropDown>

                {/* Font Family */}
                <FontFamilyToolbarPlugin />

                {/* Font Size */}
                <FontSizeToolbarPlugin />

                {/* Font Color */}
                <FontColorToolbarPlugin />

                {/* Font Background */}
                <FontBackgroundToolbarPlugin />

                {/* Element Format (Alignment + Indent) */}
                <ElementFormatToolbarPlugin />

                {/* Block Insert */}
                <BlockInsertPlugin>
                  <InsertImage />
                  <InsertTable />
                  <InsertHorizontalRule />
                  <InsertEmbeds />
                </BlockInsertPlugin>

                {/* Import/Export */}
                <div className="flex items-center gap-1 border-x px-1">
                  <ImportExportPlugin />
                  <MarkdownImportPlugin
                    transformers={PLAYGROUND_TRANSFORMERS}
                  />
                  <MarkdownTogglePlugin
                    shouldPreserveNewLinesInMarkdown={false}
                    transformers={PLAYGROUND_TRANSFORMERS}
                  />
                </div>
              </div>
            )}
          </ToolbarPlugin>
        )}

        {/* Editor Content */}
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <div className="">
                <div className="" ref={onRef}>
                  <ContentEditable
                    placeholder={placeholder}
                    className={
                      readOnly
                        ? "ContentEditable__root relative block h-auto px-0 py-4 focus:outline-none md:py-8"
                        : "ContentEditable__root relative block h-full min-h-[500px] overflow-auto px-4 py-4 focus:outline-none md:px-8 md:py-8"
                    }
                    readOnly={readOnly}
                  />
                </div>
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />

          {/* Core Plugins */}
          {!readOnly && <HistoryPlugin />}
          {!readOnly && <ListPlugin />}
          {!readOnly && <CheckListPlugin />}
          {!readOnly && <TabIndentationPlugin />}
          {!readOnly && <HorizontalRulePlugin />}
          {!readOnly && <CodeHighlightPlugin />}
          <MarkdownShortcutPlugin transformers={PLAYGROUND_TRANSFORMERS} />

          {/* Link Plugins */}
          {!readOnly && <LinkPlugin />}
          {!readOnly && <AutoLinkPlugin />}
          {!readOnly && floatingAnchorElem && (
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
            />
          )}

          {/* Image Plugin */}
          {!readOnly && <ImagesPlugin captionsEnabled={false} />}

          {/* Table Plugins */}
          {!readOnly && (
            <CustomTablePlugin
              cellEditorConfig={{
                namespace: "EssayEditor",
                nodes: [],
                onError: (error) => console.error(error),
                theme: editorTheme,
              }}
            >
              <></>
            </CustomTablePlugin>
          )}
          <TablePlugin hasCellMerge={true} hasCellBackgroundColor={true} />

          {/* Embed Plugins */}
          {!readOnly && <YouTubePlugin />}
          {!readOnly && <TwitterPlugin />}
          {!readOnly && <AutoEmbedPlugin />}

          {/* Draggable Block Plugin */}
          {!readOnly && floatingAnchorElem && (
            <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
          )}
        </div>

        {/* Modal */}
        {modal}
      </div>
    </TableContext>
  );
}
